import { RJSFSchema, UiSchema } from '@rjsf/utils';
import { JsonPlaceholderReplacer } from 'json-placeholder-replacer';
import { parseJson } from 'src/utilities/ApiHelper';
import {
  SchemaAttributes,
  SchemaTranslator,
} from 'src/integration/ddm/DDMSchemaTranslationProvider';
import { CaseCreationStaticSchema } from 'src/components/Forms/CreateCase/CreateCaseSchema';

export class SustainabilitySchemaTranslator implements SchemaTranslator {
  private coreSchema: RJSFSchema;
  private uiSchema: UiSchema;
  private readonly caseSummarySchema: RJSFSchema;
  private createCaseStaticSchema: CaseCreationStaticSchema;
  private bundles: any;

  constructor(schemaAttributes: SchemaAttributes) {
    this.coreSchema = schemaAttributes.coreSchema;
    this.uiSchema = schemaAttributes.uiSchema;
    this.caseSummarySchema = schemaAttributes.caseSummarySchema;
    this.createCaseStaticSchema = schemaAttributes.createCaseStaticSchema;
    this.bundles = schemaAttributes.bundles;
  }

  translateCoreSchema(): RJSFSchema {
    return this.getPlaceholderReplacedCoreSchema(
      this.coreSchema,
      this.bundles.socialResponsibilityBundle,
    );
  }
  translateUiSchema(): UiSchema {
    return this.getPlaceholderReplacedUiSchema(
      this.uiSchema,
      this.bundles.socialResponsibilityBundle,
    );
  }
  translateCreateCaseStaticSchema(): CaseCreationStaticSchema {
    return this.getPlaceholderReplacedCreateCaseStaticSchema(
      this.createCaseStaticSchema,
      this.bundles.socialResponsibilityBundle,
    );
  }

  translateCaseSummarySchema(): RJSFSchema {
    return this.caseSummarySchema;
  }

  private getPlaceholderReplacedCoreSchema(
    schema: RJSFSchema,
    bundle: any,
  ): RJSFSchema {
    const placeHolderReplacer = new JsonPlaceholderReplacer();
    placeHolderReplacer.addVariableMap({
      select_one_option_name: bundle.getMessage('select_one_option_name'),
      case_title_field_name: bundle.getMessage('case_title_field_name'),
      incident_category_field_name: bundle.getMessage(
        'incident_category_field_name',
      ),
      incident_category_option_child_labor_and_young_workers: bundle.getMessage(
        'incident_category_option_child_labor_and_young_workers',
      ),
      incident_category_option_forced_labor_and_modern_slavery:
        bundle.getMessage(
          'incident_category_option_forced_labor_and_modern_slavery',
        ),
      incident_category_option_occupational_health_and_safety:
        bundle.getMessage(
          'incident_category_option_occupational_health_and_safety',
        ),
      incident_category_option_wages_and_benefits: bundle.getMessage(
        'incident_category_option_wages_and_benefits',
      ),
      incident_category_option_working_hours: bundle.getMessage(
        'incident_category_option_working_hours',
      ),
      incident_category_option_discrimination_and_harassment: bundle.getMessage(
        'incident_category_option_discrimination_and_harassment',
      ),
      incident_category_option_unethical_behavior_and_corruption:
        bundle.getMessage(
          'incident_category_option_unethical_behavior_and_corruption',
        ),
      incident_category_option_freedom_of_association_and_collective_bargaining:
        bundle.getMessage(
          'incident_category_option_freedom_of_association_and_collective_bargaining',
        ),
      incident_category_option_land_and_natural_resources: bundle.getMessage(
        'incident_category_option_land_and_natural_resources',
      ),
      incident_category_option_security_and_data_privacy: bundle.getMessage(
        'incident_category_option_security_and_data_privacy',
      ),
      incident_category_option_hazardous_waste_management_and_disposal:
        bundle.getMessage(
          'incident_category_option_hazardous_waste_management_and_disposal',
        ),
      incident_category_option_other: bundle.getMessage(
        'incident_category_option_other',
      ),
      incident_occurred_at_field_name: bundle.getMessage(
        'incident_occurred_at_field_name',
      ),
      incident_occurred_at_option_amazon_site: bundle.getMessage(
        'incident_occurred_at_option_amazon_site',
      ),
      incident_occurred_at_option_supplier_site: bundle.getMessage(
        'incident_occurred_at_option_supplier_site',
      ),
      incident_occurred_at_option_other_unknown: bundle.getMessage(
        'incident_occurred_at_option_other_unknown',
      ),
      incident_location_field_name: bundle.getMessage(
        'incident_location_field_name',
      ),
      incident_location_field_description: bundle.getMessage(
        'incident_location_field_description',
      ),
      country_field_name: bundle.getMessage('country_field_name'),
      city_field_name: bundle.getMessage('city_field_name'),
      state_field_name: bundle.getMessage('state_field_name'),
      zip_postal_code_field_name: bundle.getMessage(
        'zip_postal_code_field_name',
      ),
      relationship_to_amazon_field_name: bundle.getMessage(
        'relationship_to_amazon_field_name',
      ),
      relationship_to_amazon_option_employee: bundle.getMessage(
        'relationship_to_amazon_option_employee',
      ),
      relationship_to_amazon_option_customer: bundle.getMessage(
        'relationship_to_amazon_option_customer',
      ),
      relationship_to_amazon_option_supplier: bundle.getMessage(
        'relationship_to_amazon_option_supplier',
      ),
      relationship_to_amazon_option_seller: bundle.getMessage(
        'relationship_to_amazon_option_seller',
      ),
      relationship_to_amazon_option_contractor: bundle.getMessage(
        'relationship_to_amazon_option_contractor',
      ),
      relationship_to_amazon_option_former_employee: bundle.getMessage(
        'relationship_to_amazon_option_former_employee',
      ),
      relationship_to_amazon_option_other: bundle.getMessage(
        'relationship_to_amazon_option_other',
      ),
      report_header: bundle.getMessage('report_header'),
      describe_the_incident_field_name: bundle.getMessage(
        'describe_the_incident_field_name',
      ),
      describe_the_incident_field_description: bundle.getMessage(
        'describe_the_incident_field_description',
      ),
      where_did_this_incident_occur_field_name: bundle.getMessage(
        'where_did_this_incident_occur_field_name',
      ),
      where_did_this_incident_occur_field_description: bundle.getMessage(
        'where_did_this_incident_occur_field_description',
      ),
      when_did_this_incident_occur_field_name: bundle.getMessage(
        'when_did_this_incident_occur_field_name',
      ),
      when_did_this_incident_occur_field_description: bundle.getMessage(
        'when_did_this_incident_occur_field_description',
      ),
      incident_duration_field_name: bundle.getMessage(
        'incident_duration_field_name',
      ),
      incident_duration_option_once: bundle.getMessage(
        'incident_duration_option_once',
      ),
      incident_duration_option_one_week: bundle.getMessage(
        'incident_duration_option_one_week',
      ),
      incident_duration_option_1_3_months: bundle.getMessage(
        'incident_duration_option_1_3_months',
      ),
      incident_duration_option_3_months_to_1_year: bundle.getMessage(
        'incident_duration_option_3_months_to_1_year',
      ),
      incident_duration_option_more_than_1_year: bundle.getMessage(
        'incident_duration_option_more_than_1_year',
      ),
      incident_duration_option_i_do_not_know: bundle.getMessage(
        'incident_duration_option_i_do_not_know',
      ),
      is_it_still_ongoing_field_name: bundle.getMessage(
        'is_it_still_ongoing_field_name',
      ),
      is_it_still_ongoing_option_yes: bundle.getMessage(
        'is_it_still_ongoing_option_yes',
      ),
      is_it_still_ongoing_option_no: bundle.getMessage(
        'is_it_still_ongoing_option_no',
      ),
      is_it_still_ongoing_option_i_do_not_know: bundle.getMessage(
        'is_it_still_ongoing_option_i_do_not_know',
      ),
      incident_awareness_field_name: bundle.getMessage(
        'incident_awareness_field_name',
      ),
      incident_awareness_option_it_happened_to_me: bundle.getMessage(
        'incident_awareness_option_it_happened_to_me',
      ),
      incident_awareness_option_observed: bundle.getMessage(
        'incident_awareness_option_observed',
      ),
      incident_awareness_option_be_told_by_employee: bundle.getMessage(
        'incident_awareness_option_be_told_by_employee',
      ),
      incident_awareness_option_told_by_someone_outside: bundle.getMessage(
        'incident_awareness_option_told_by_someone_outside',
      ),
      incident_awareness_option_found_file: bundle.getMessage(
        'incident_awareness_option_found_file',
      ),
      incident_awareness_option_other: bundle.getMessage(
        'incident_awareness_option_other',
      ),
      incident_awareness_option_other_details: bundle.getMessage(
        'incident_awareness_option_other_details',
      ),
      agree_to_terms_and_conditions_checkbox: bundle.getMessage(
        'agree_to_terms_and_conditions_checkbox',
      ),
      who_is_the_supplier_field_name: bundle.getMessage(
        'who_is_the_supplier_field_name',
      ),
      if_other_please_explain_your_relationship_to_amazon_field_name:
        bundle.getMessage(
          'if_other_please_explain_your_relationship_to_amazon_field_name',
        ),
      country_option_afghanistan: bundle.getMessage(
        'country_option_afghanistan',
      ),
      country_option_aland_islands: bundle.getMessage(
        'country_option_aland_islands',
      ),
      country_option_albania: bundle.getMessage('country_option_albania'),
      country_option_algeria: bundle.getMessage('country_option_algeria'),
      country_option_american_samoa: bundle.getMessage(
        'country_option_american_samoa',
      ),
      country_option_andorra: bundle.getMessage('country_option_andorra'),
      country_option_angola: bundle.getMessage('country_option_angola'),
      country_option_anguilla: bundle.getMessage('country_option_anguilla'),
      country_option_antarctica: bundle.getMessage('country_option_antarctica'),
      country_option_antigua_and_barbuda: bundle.getMessage(
        'country_option_antigua_and_barbuda',
      ),
      country_option_argentina: bundle.getMessage('country_option_argentina'),
      country_option_armenia: bundle.getMessage('country_option_armenia'),
      country_option_aruba: bundle.getMessage('country_option_aruba'),
      country_option_australia: bundle.getMessage('country_option_australia'),
      country_option_austria: bundle.getMessage('country_option_austria'),
      country_option_azerbaijan: bundle.getMessage('country_option_azerbaijan'),
      country_option_bahamas_the: bundle.getMessage(
        'country_option_bahamas_the',
      ),
      country_option_bahrain: bundle.getMessage('country_option_bahrain'),
      country_option_bangladesh: bundle.getMessage('country_option_bangladesh'),
      country_option_barbados: bundle.getMessage('country_option_barbados'),
      country_option_belarus: bundle.getMessage('country_option_belarus'),
      country_option_belgium: bundle.getMessage('country_option_belgium'),
      country_option_belize: bundle.getMessage('country_option_belize'),
      country_option_benin: bundle.getMessage('country_option_benin'),
      country_option_bermuda: bundle.getMessage('country_option_bermuda'),
      country_option_bhutan: bundle.getMessage('country_option_bhutan'),
      country_option_bolivia: bundle.getMessage('country_option_bolivia'),
      country_option_bonaire_saint_eustatius_and_saba: bundle.getMessage(
        'country_option_bonaire_saint_eustatius_and_saba',
      ),
      country_option_bosnia_and_herzegovina: bundle.getMessage(
        'country_option_bosnia_and_herzegovina',
      ),
      country_option_botswana: bundle.getMessage('country_option_botswana'),
      country_option_bouvet_island: bundle.getMessage(
        'country_option_bouvet_island',
      ),
      country_option_brazil: bundle.getMessage('country_option_brazil'),
      country_option_british_indian_ocean_territory: bundle.getMessage(
        'country_option_british_indian_ocean_territory',
      ),
      country_option_brunei_darussalam: bundle.getMessage(
        'country_option_brunei_darussalam',
      ),
      country_option_bulgaria: bundle.getMessage('country_option_bulgaria'),
      country_option_burkina_faso: bundle.getMessage(
        'country_option_burkina_faso',
      ),
      country_option_burundi: bundle.getMessage('country_option_burundi'),
      country_option_cambodia: bundle.getMessage('country_option_cambodia'),
      country_option_cameroon: bundle.getMessage('country_option_cameroon'),
      country_option_canada: bundle.getMessage('country_option_canada'),
      country_option_canary_islands: bundle.getMessage(
        'country_option_canary_islands',
      ),
      country_option_cape_verde: bundle.getMessage('country_option_cape_verde'),
      country_option_cayman_islands: bundle.getMessage(
        'country_option_cayman_islands',
      ),
      country_option_central_african_republic: bundle.getMessage(
        'country_option_central_african_republic',
      ),
      country_option_chad: bundle.getMessage('country_option_chad'),
      country_option_chile: bundle.getMessage('country_option_chile'),
      country_option_china: bundle.getMessage('country_option_china'),
      country_option_christmas_island: bundle.getMessage(
        'country_option_christmas_island',
      ),
      country_option_cocos_keeling_islands: bundle.getMessage(
        'country_option_cocos_keeling_islands',
      ),
      country_option_colombia: bundle.getMessage('country_option_colombia'),
      country_option_comoros: bundle.getMessage('country_option_comoros'),
      country_option_congo: bundle.getMessage('country_option_congo'),
      country_option_congo_the_democratic_republic_of_the: bundle.getMessage(
        'country_option_congo_the_democratic_republic_of_the',
      ),
      country_option_cook_islands: bundle.getMessage(
        'country_option_cook_islands',
      ),
      country_option_costa_rica: bundle.getMessage('country_option_costa_rica'),
      country_option_cote_divoire: bundle.getMessage(
        'country_option_cote_divoire',
      ),
      country_option_croatia: bundle.getMessage('country_option_croatia'),
      country_option_curacao: bundle.getMessage('country_option_curacao'),
      country_option_cyprus: bundle.getMessage('country_option_cyprus'),
      country_option_czech_republic: bundle.getMessage(
        'country_option_czech_republic',
      ),
      country_option_denmark: bundle.getMessage('country_option_denmark'),
      country_option_djibouti: bundle.getMessage('country_option_djibouti'),
      country_option_dominica: bundle.getMessage('country_option_dominica'),
      country_option_dominican_republic: bundle.getMessage(
        'country_option_dominican_republic',
      ),
      country_option_ecuador: bundle.getMessage('country_option_ecuador'),
      country_option_egypt: bundle.getMessage('country_option_egypt'),
      country_option_el_salvador: bundle.getMessage(
        'country_option_el_salvador',
      ),
      country_option_equatorial_guinea: bundle.getMessage(
        'country_option_equatorial_guinea',
      ),
      country_option_eritrea: bundle.getMessage('country_option_eritrea'),
      country_option_estonia: bundle.getMessage('country_option_estonia'),
      country_option_ethiopia: bundle.getMessage('country_option_ethiopia'),
      country_option_falkland_islands_malvinas: bundle.getMessage(
        'country_option_falkland_islands_malvinas',
      ),
      country_option_faroe_islands: bundle.getMessage(
        'country_option_faroe_islands',
      ),
      country_option_fiji: bundle.getMessage('country_option_fiji'),
      country_option_finland: bundle.getMessage('country_option_finland'),
      country_option_france: bundle.getMessage('country_option_france'),
      country_option_french_guiana: bundle.getMessage(
        'country_option_french_guiana',
      ),
      country_option_french_polynesia: bundle.getMessage(
        'country_option_french_polynesia',
      ),
      country_option_french_southern_territories: bundle.getMessage(
        'country_option_french_southern_territories',
      ),
      country_option_gabon: bundle.getMessage('country_option_gabon'),
      country_option_gambia_the: bundle.getMessage('country_option_gambia_the'),
      country_option_georgia: bundle.getMessage('country_option_georgia'),
      country_option_germany: bundle.getMessage('country_option_germany'),
      country_option_ghana: bundle.getMessage('country_option_ghana'),
      country_option_gibraltar: bundle.getMessage('country_option_gibraltar'),
      country_option_greece: bundle.getMessage('country_option_greece'),
      country_option_greenland: bundle.getMessage('country_option_greenland'),
      country_option_grenada: bundle.getMessage('country_option_grenada'),
      country_option_guadeloupe: bundle.getMessage('country_option_guadeloupe'),
      country_option_guam: bundle.getMessage('country_option_guam'),
      country_option_guatemala: bundle.getMessage('country_option_guatemala'),
      country_option_guernsey: bundle.getMessage('country_option_guernsey'),
      country_option_guinea: bundle.getMessage('country_option_guinea'),
      country_option_guinea_bissau: bundle.getMessage(
        'country_option_guinea_bissau',
      ),
      country_option_guyana: bundle.getMessage('country_option_guyana'),
      country_option_haiti: bundle.getMessage('country_option_haiti'),
      country_option_heard_island_and_the_mc_donald_islands: bundle.getMessage(
        'country_option_heard_island_and_the_mc_donald_islands',
      ),
      country_option_holy_see: bundle.getMessage('country_option_holy_see'),
      country_option_honduras: bundle.getMessage('country_option_honduras'),
      country_option_hong_kong: bundle.getMessage('country_option_hong_kong'),
      country_option_hungary: bundle.getMessage('country_option_hungary'),
      country_option_iceland: bundle.getMessage('country_option_iceland'),
      country_option_india: bundle.getMessage('country_option_india'),
      country_option_indonesia: bundle.getMessage('country_option_indonesia'),
      country_option_iraq: bundle.getMessage('country_option_iraq'),
      country_option_ireland: bundle.getMessage('country_option_ireland'),
      country_option_isle_of_man: bundle.getMessage(
        'country_option_isle_of_man',
      ),
      country_option_israel: bundle.getMessage('country_option_israel'),
      country_option_italy: bundle.getMessage('country_option_italy'),
      country_option_jamaica: bundle.getMessage('country_option_jamaica'),
      country_option_japan: bundle.getMessage('country_option_japan'),
      country_option_jersey: bundle.getMessage('country_option_jersey'),
      country_option_jordan: bundle.getMessage('country_option_jordan'),
      country_option_kazakhstan: bundle.getMessage('country_option_kazakhstan'),
      country_option_kenya: bundle.getMessage('country_option_kenya'),
      country_option_kiribati: bundle.getMessage('country_option_kiribati'),
      country_option_kosovo: bundle.getMessage('country_option_kosovo'),
      country_option_kuwait: bundle.getMessage('country_option_kuwait'),
      country_option_kyrgyzstan: bundle.getMessage('country_option_kyrgyzstan'),
      country_option_lao_peoples_democratic_republic: bundle.getMessage(
        'country_option_lao_peoples_democratic_republic',
      ),
      country_option_latvia: bundle.getMessage('country_option_latvia'),
      country_option_lebanon: bundle.getMessage('country_option_lebanon'),
      country_option_lesotho: bundle.getMessage('country_option_lesotho'),
      country_option_liberia: bundle.getMessage('country_option_liberia'),
      country_option_libya: bundle.getMessage('country_option_libya'),
      country_option_liechtenstein: bundle.getMessage(
        'country_option_liechtenstein',
      ),
      country_option_lithuania: bundle.getMessage('country_option_lithuania'),
      country_option_luxembourg: bundle.getMessage('country_option_luxembourg'),
      country_option_macao: bundle.getMessage('country_option_macao'),
      country_option_macedonia_the_former_yugoslav_republic_of:
        bundle.getMessage(
          'country_option_macedonia_the_former_yugoslav_republic_of',
        ),
      country_option_madagascar: bundle.getMessage('country_option_madagascar'),
      country_option_malawi: bundle.getMessage('country_option_malawi'),
      country_option_malaysia: bundle.getMessage('country_option_malaysia'),
      country_option_maldives: bundle.getMessage('country_option_maldives'),
      country_option_mali: bundle.getMessage('country_option_mali'),
      country_option_malta: bundle.getMessage('country_option_malta'),
      country_option_marshall_islands: bundle.getMessage(
        'country_option_marshall_islands',
      ),
      country_option_martinique: bundle.getMessage('country_option_martinique'),
      country_option_mauritania: bundle.getMessage('country_option_mauritania'),
      country_option_mauritius: bundle.getMessage('country_option_mauritius'),
      country_option_mayotte: bundle.getMessage('country_option_mayotte'),
      country_option_mexico: bundle.getMessage('country_option_mexico'),
      country_option_micronesia_federated_states_of: bundle.getMessage(
        'country_option_micronesia_federated_states_of',
      ),
      country_option_moldova_republic_of: bundle.getMessage(
        'country_option_moldova_republic_of',
      ),
      country_option_monaco: bundle.getMessage('country_option_monaco'),
      country_option_mongolia: bundle.getMessage('country_option_mongolia'),
      country_option_montenegro: bundle.getMessage('country_option_montenegro'),
      country_option_montserrat: bundle.getMessage('country_option_montserrat'),
      country_option_morocco: bundle.getMessage('country_option_morocco'),
      country_option_mozambique: bundle.getMessage('country_option_mozambique'),
      country_option_myanmar: bundle.getMessage('country_option_myanmar'),
      country_option_namibia: bundle.getMessage('country_option_namibia'),
      country_option_nauru: bundle.getMessage('country_option_nauru'),
      country_option_nepal: bundle.getMessage('country_option_nepal'),
      country_option_netherlands: bundle.getMessage(
        'country_option_netherlands',
      ),
      country_option_netherlands_antilles: bundle.getMessage(
        'country_option_netherlands_antilles',
      ),
      country_option_new_caledonia: bundle.getMessage(
        'country_option_new_caledonia',
      ),
      country_option_new_zealand: bundle.getMessage(
        'country_option_new_zealand',
      ),
      country_option_nicaragua: bundle.getMessage('country_option_nicaragua'),
      country_option_niger: bundle.getMessage('country_option_niger'),
      country_option_nigeria: bundle.getMessage('country_option_nigeria'),
      country_option_niue: bundle.getMessage('country_option_niue'),
      country_option_norfolk_island: bundle.getMessage(
        'country_option_norfolk_island',
      ),
      country_option_northern_mariana_islands: bundle.getMessage(
        'country_option_northern_mariana_islands',
      ),
      country_option_norway: bundle.getMessage('country_option_norway'),
      country_option_oman: bundle.getMessage('country_option_oman'),
      country_option_pakistan: bundle.getMessage('country_option_pakistan'),
      country_option_palau: bundle.getMessage('country_option_palau'),
      country_option_palestinian_territories: bundle.getMessage(
        'country_option_palestinian_territories',
      ),
      country_option_panama: bundle.getMessage('country_option_panama'),
      country_option_papua_new_guinea: bundle.getMessage(
        'country_option_papua_new_guinea',
      ),
      country_option_paraguay: bundle.getMessage('country_option_paraguay'),
      country_option_peru: bundle.getMessage('country_option_peru'),
      country_option_philippines: bundle.getMessage(
        'country_option_philippines',
      ),
      country_option_pitcairn: bundle.getMessage('country_option_pitcairn'),
      country_option_poland: bundle.getMessage('country_option_poland'),
      country_option_portugal: bundle.getMessage('country_option_portugal'),
      country_option_puerto_rico: bundle.getMessage(
        'country_option_puerto_rico',
      ),
      country_option_qatar: bundle.getMessage('country_option_qatar'),
      country_option_republic_of_korea: bundle.getMessage(
        'country_option_republic_of_korea',
      ),
      country_option_reunion: bundle.getMessage('country_option_reunion'),
      country_option_romania: bundle.getMessage('country_option_romania'),
      country_option_russian_federation: bundle.getMessage(
        'country_option_russian_federation',
      ),
      country_option_rwanda: bundle.getMessage('country_option_rwanda'),
      country_option_saint_barthelemy: bundle.getMessage(
        'country_option_saint_barthelemy',
      ),
      country_option_saint_helena_ascension_and_tristan_da_cunha:
        bundle.getMessage(
          'country_option_saint_helena_ascension_and_tristan_da_cunha',
        ),
      country_option_saint_kitts_and_nevis: bundle.getMessage(
        'country_option_saint_kitts_and_nevis',
      ),
      country_option_saint_lucia: bundle.getMessage(
        'country_option_saint_lucia',
      ),
      country_option_saint_martin: bundle.getMessage(
        'country_option_saint_martin',
      ),
      country_option_saint_pierre_and_miquelon: bundle.getMessage(
        'country_option_saint_pierre_and_miquelon',
      ),
      country_option_saint_vincent_and_the_grenadines: bundle.getMessage(
        'country_option_saint_vincent_and_the_grenadines',
      ),
      country_option_samoa: bundle.getMessage('country_option_samoa'),
      country_option_san_marino: bundle.getMessage('country_option_san_marino'),
      country_option_sao_tome_and_principe: bundle.getMessage(
        'country_option_sao_tome_and_principe',
      ),
      country_option_saudi_arabia: bundle.getMessage(
        'country_option_saudi_arabia',
      ),
      country_option_senegal: bundle.getMessage('country_option_senegal'),
      country_option_serbia: bundle.getMessage('country_option_serbia'),
      country_option_seychelles: bundle.getMessage('country_option_seychelles'),
      country_option_sierra_leone: bundle.getMessage(
        'country_option_sierra_leone',
      ),
      country_option_singapore: bundle.getMessage('country_option_singapore'),
      country_option_sint_maarten: bundle.getMessage(
        'country_option_sint_maarten',
      ),
      country_option_slovakia: bundle.getMessage('country_option_slovakia'),
      country_option_slovenia: bundle.getMessage('country_option_slovenia'),
      country_option_solomon_islands: bundle.getMessage(
        'country_option_solomon_islands',
      ),
      country_option_somalia: bundle.getMessage('country_option_somalia'),
      country_option_south_africa: bundle.getMessage(
        'country_option_south_africa',
      ),
      country_option_south_georgia_and_the_south_sandwich_islands:
        bundle.getMessage(
          'country_option_south_georgia_and_the_south_sandwich_islands',
        ),
      country_option_spain: bundle.getMessage('country_option_spain'),
      country_option_sri_lanka: bundle.getMessage('country_option_sri_lanka'),
      country_option_suriname: bundle.getMessage('country_option_suriname'),
      country_option_svalbard_and_jan_mayen: bundle.getMessage(
        'country_option_svalbard_and_jan_mayen',
      ),
      country_option_swaziland: bundle.getMessage('country_option_swaziland'),
      country_option_sweden: bundle.getMessage('country_option_sweden'),
      country_option_switzerland: bundle.getMessage(
        'country_option_switzerland',
      ),
      country_option_taiwan: bundle.getMessage('country_option_taiwan'),
      country_option_tajikistan: bundle.getMessage('country_option_tajikistan'),
      country_option_tanzania_united_republic_of: bundle.getMessage(
        'country_option_tanzania_united_republic_of',
      ),
      country_option_thailand: bundle.getMessage('country_option_thailand'),
      country_option_timor_leste: bundle.getMessage(
        'country_option_timor_leste',
      ),
      country_option_togo: bundle.getMessage('country_option_togo'),
      country_option_tokelau: bundle.getMessage('country_option_tokelau'),
      country_option_tonga: bundle.getMessage('country_option_tonga'),
      country_option_trinidad_and_tobago: bundle.getMessage(
        'country_option_trinidad_and_tobago',
      ),
      country_option_tunisia: bundle.getMessage('country_option_tunisia'),
      country_option_turkey: bundle.getMessage('country_option_turkey'),
      country_option_turkmenistan: bundle.getMessage(
        'country_option_turkmenistan',
      ),
      country_option_turks_and_caicos_islands: bundle.getMessage(
        'country_option_turks_and_caicos_islands',
      ),
      country_option_tuvalu: bundle.getMessage('country_option_tuvalu'),
      country_option_uganda: bundle.getMessage('country_option_uganda'),
      country_option_ukraine: bundle.getMessage('country_option_ukraine'),
      country_option_united_arab_emirates: bundle.getMessage(
        'country_option_united_arab_emirates',
      ),
      country_option_united_kingdom: bundle.getMessage(
        'country_option_united_kingdom',
      ),
      country_option_united_states: bundle.getMessage(
        'country_option_united_states',
      ),
      country_option_united_states_minor_outlying_islands: bundle.getMessage(
        'country_option_united_states_minor_outlying_islands',
      ),
      country_option_uruguay: bundle.getMessage('country_option_uruguay'),
      country_option_uzbekistan: bundle.getMessage('country_option_uzbekistan'),
      country_option_vanuatu: bundle.getMessage('country_option_vanuatu'),
      country_option_venezuela: bundle.getMessage('country_option_venezuela'),
      country_option_vietnam: bundle.getMessage('country_option_vietnam'),
      country_option_virgin_islands_british: bundle.getMessage(
        'country_option_virgin_islands_british',
      ),
      country_option_virgin_islands_u_s: bundle.getMessage(
        'country_option_virgin_islands_u_s',
      ),
      country_option_wallis_and_futuna: bundle.getMessage(
        'country_option_wallis_and_futuna',
      ),
      country_option_western_sahara: bundle.getMessage(
        'country_option_western_sahara',
      ),
      country_option_yemen: bundle.getMessage('country_option_yemen'),
      country_option_zambia: bundle.getMessage('country_option_zambia'),
      country_option_zimbabwe: bundle.getMessage('country_option_zimbabwe'),
    });

    return placeHolderReplacer.replace(parseJson(JSON.stringify(schema)));
  }

  private getPlaceholderReplacedUiSchema(
    schema: UiSchema,
    bundle: any,
  ): UiSchema {
    const placeHolderReplacer = new JsonPlaceholderReplacer();

    placeHolderReplacer.addVariableMap({
      mandatory_field_error_message: bundle.getMessage(
        'mandatory_field_error_message',
      ),
      agree_to_terms_and_condition_description: bundle.getMessage(
        'agree_to_terms_and_condition_description',
      ),
    });

    return placeHolderReplacer.replace(parseJson(JSON.stringify(schema)));
  }

  private getPlaceholderReplacedCreateCaseStaticSchema(
    schema: CaseCreationStaticSchema,
    bundle: any,
  ): CaseCreationStaticSchema {
    const placeHolderReplacer = new JsonPlaceholderReplacer();

    placeHolderReplacer.addVariableMap({
      referral_information_section_header: bundle.getMessage(
        'referral_information_section_header',
      ),
      referral_information_full_name_field_name: bundle.getMessage(
        'referral_information_full_name_field_name',
      ),
      referral_information_email_field_name: bundle.getMessage(
        'referral_information_email_field_name',
      ),
    });

    return placeHolderReplacer.replace(
      parseJson(JSON.stringify(schema)),
    ) as CaseCreationStaticSchema;
  }
}
